/**
 * Agent notes — the memory surface (**O4**).
 *
 * Every note is an ordinary document in a visible folder, so the user can read
 * and delete anything an agent wrote from Apiboo's own sidebar. There is no
 * hidden store and no memory record type.
 *
 * `memory_append` has its own capability key so a user can grant an agent a
 * scratchpad without granting write access to their documents.
 *
 * **NOT built, deliberately:** embeddings or vector search; cross-scope
 * search; automatic memory (Apiboo never writes a note on its own — the agent
 * asks); any memory sync semantics beyond what documents already have.
 */
import { readDocument } from '../../extensions/bundled/documents/documentsStore';
import {
  ensureNote,
  findMemoryFolder,
  listNotes,
  deleteNodes,
  listTree,
} from '../../extensions/bundled/documents/documentsMemory';
import { markdownToHtml } from '../../utils/markdownToHtml';
import { sanitizeRichTextHtml } from '../../utils/sanitizeHtml';
import { renderBody } from '../markdown';
import { documentUri, resourceLink } from '../resources';
import { toDocScope, PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { documentWrite } from './documents';

/** i18n-free default: the folder name is passed in by the bus (see bus.js). */
export function defaultNoteTitle(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export async function memoryList({ scope }, { folderName }) {
  const wire = scope || PERSONAL;
  const { folder, notes } = await listNotes(toDocScope(wire));
  return {
    scope: wire,
    folder: folder ? { id: folder.id, name: folder.name } : null,
    folderName,
    notes: notes.map((n) => ({ id: n.id, title: n.name, uri: documentUri(wire, n.id) })),
    resourceLinks: notes.map((n) => resourceLink(documentUri(wire, n.id), n.name || n.id)),
    scopesConsulted: [wire],
    scopesUnavailable: [],
  };
}

export async function memoryRead({ note_id: noteId, scope }) {
  const wire = scope || PERSONAL;
  const folder = await findMemoryFolder(toDocScope(wire));
  if (!folder) return mcpError('not_found', 'This scope has no agent notes yet.');
  const tree = await listTree(toDocScope(wire));
  const node = tree.find((n) => n.id === String(noteId) && n.parentId === folder.id);
  // A note id that is not inside the notes folder is NOT readable through the
  // memory surface — otherwise `memory` would be a second, unlabelled grant
  // over the whole documents tree.
  if (!node) return mcpError('not_found', 'No agent note with that id in this scope.');
  const doc = await readDocument(node.id, toDocScope(wire));
  if (!doc) return mcpError('not_found', 'No agent note with that id in this scope.');
  return {
    id: doc.id,
    title: doc.title,
    text: renderBody(doc.bodyHtml, 'markdown'),
    scope: wire,
    uri: documentUri(wire, doc.id),
    agentNote: true,
  };
}

export async function memoryAppend({ text, title, scope }, ctx) {
  const { folderName } = ctx || {};
  const wire = scope || PERSONAL;
  const noteTitle = String(title || defaultNoteTitle());
  const { note, created } = await ensureNote(toDocScope(wire), folderName, noteTitle);
  // Reuse the documents append path so the CAS retry loop, the live-editor
  // rule and the 32 KB pre-check are the SAME code — a second append
  // implementation is a second place for the oversize trap to be forgotten.
  // `ctx` carries `caller`, so the note is marked as written by this MCP
  // client (AI Act Art. 50(2), services/ai/aiAuthorship.js).
  const result = await documentWrite(
    {
      document_id: note.id,
      content: String(text ?? ''),
      mode: 'append',
      format: 'markdown',
      scope: wire,
    },
    ctx
  );
  if (result.error) return result;
  return { ...result, id: note.id, title: noteTitle, created, agentNote: true };
}

export async function memoryPurge({ scope }) {
  const wire = scope || PERSONAL;
  const folder = await findMemoryFolder(toDocScope(wire));
  if (!folder) return { scope: wire, deleted: 0 };
  const { notes } = await listNotes(toDocScope(wire));
  await deleteNodes(
    [...notes.map((n) => ({ id: n.id, type: 'document' })), { id: folder.id, type: 'folder' }],
    toDocScope(wire)
  );
  return { scope: wire, deleted: notes.length + 1 };
}

/** Only used when the caller supplied no markdown converter; kept so the
 *  module's imports are honest about what it can produce. */
export function noteHtml(markdown) {
  return sanitizeRichTextHtml(markdownToHtml(String(markdown ?? '')));
}
